import {buildListingAddress} from "@hosttools/core/shared/utils/utils.listing";
import {
    Box,
    Checkbox,
    CheckboxIcon,
    CheckboxIndicator,
    FormField,
    Input,
    InputField,
    InputSlot,
    SelectGeneric,
    VStack
} from "@hosttools/frontend/components";
import type {Listing} from "@hosttools/frontend/models/listing";
import CountryRegionData from "country-region-data/data.json";
import type React from "react";
import {memo, useEffect, useMemo, useState} from "react";
import {Controller, useFormContext} from "react-hook-form";

const Location: React.FC = () => {
    const {
        control,
        watch,
        register,
        formState: {errors: formErrors},
        setValue
    } = useFormContext<PartiallyOptional<Listing, "location">>();

    // Watch address fields for changes
    const streetNumber = watch("streetNumber");
    const streetName = watch("streetName");
    const apartment = watch("apartment");
    const city = watch("city");
    const region = watch("region");
    const zipcode = watch("zipcode");
    const country = watch("country");
    const airbnbAddress = watch("airbnbAddress");

    // State for debounced values
    const [debouncedAddress, setDebouncedAddress] = useState<string>("");

    const [isCustomAddress, setIsCustomAddress] = useState(
        buildListingAddress({
            streetNumber,
            streetName,
            apartment,
            city,
            region,
            zipcode,
            country
        }) !== airbnbAddress
    );

    // Debounce the address values
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAddress(
                buildListingAddress({
                    streetNumber: streetNumber || "",
                    streetName: streetName || "",
                    apartment: apartment || "",
                    city: city || "",
                    region: region || "",
                    zipcode: zipcode || "",
                    country: country || ""
                })
            );
        }, 1500); // 1.5 second delay

        return () => clearTimeout(timer);
    }, [streetNumber, streetName, apartment, city, region, zipcode, country]);

    const optionsCountry = useMemo(() => {
        return CountryRegionData.map(country => {
            return {
                // we should use the country code rather than the country name
                value: country.countryName,
                label: country.countryName
            };
        });
    }, []);

    useEffect(() => {
        const subscription = watch(
            (
                {
                    city,
                    region,
                    country,
                    streetName,
                    zipcode,
                    streetNumber,
                    apartment,
                    airbnbAddress
                },
                {name}
            ) => {
                // Skip if we're in custom address mode or if the change was to airbnbAddress itself
                if (isCustomAddress || !name || name === "airbnbAddress") {
                    return;
                }

                const newAirbnbAddress = buildListingAddress({
                    streetNumber,
                    streetName,
                    apartment,
                    zipcode,
                    city,
                    region,
                    country
                });

                // Only update if the address has actually changed from the previous value
                if (newAirbnbAddress !== airbnbAddress) {
                    setValue("airbnbAddress", newAirbnbAddress);
                }
            }
        );

        return () => subscription.unsubscribe();
    }, [watch, isCustomAddress, setValue]);

    useEffect(() => {
        if (!isCustomAddress) {
            const newAirbnbAddress = buildListingAddress({
                streetNumber,
                streetName,
                apartment,
                zipcode,
                city,
                region,
                country
            });

            // Only update if the address has actually changed from the previous value
            if (newAirbnbAddress !== airbnbAddress) {
                setValue("airbnbAddress", newAirbnbAddress);
            }
        }
    }, [
        isCustomAddress,
        streetNumber,
        streetName,
        apartment,
        city,
        region,
        zipcode,
        country,
        airbnbAddress,
        setValue
    ]);

    return (
        <VStack space="lg">
            <VStack className="md:flex-row z-auto" space="lg">
                <FormField
                    isInsideTab
                    error={formErrors.streetNumber?.message}
                    label="Street Number"
                    name="streetNumber"
                >
                    <Controller
                        control={control}
                        name="streetNumber"
                        render={({field: {onChange, value}}) => {
                            return (
                                <Input isDisabled={isCustomAddress}>
                                    <InputField
                                        id="streetNumber"
                                        testID="streetNumber"
                                        type="text"
                                        value={value}
                                        onChangeText={onChange}
                                    />
                                </Input>
                            );
                        }}
                        rules={{
                            required: isCustomAddress ? false : "Street number is required"
                        }}
                    />
                </FormField>
                <FormField
                    isInsideTab
                    error={formErrors.streetName?.message}
                    label="Street Name"
                    name="streetName"
                >
                    <Controller
                        control={control}
                        name="streetName"
                        render={({field: {onChange, value}}) => {
                            return (
                                <Input isDisabled={isCustomAddress}>
                                    <InputField
                                        id="streetName"
                                        testID="streetName"
                                        type="text"
                                        value={value}
                                        onChangeText={onChange}
                                    />
                                </Input>
                            );
                        }}
                        rules={{
                            required: isCustomAddress ? false : "Street name is required"
                        }}
                    />
                </FormField>
                <FormField isInsideTab label="Apartment" name="apartment">
                    <Input isDisabled={isCustomAddress}>
                        <InputField
                            id="apartment"
                            testID="apartment"
                            type="text"
                            {...register("apartment")}
                        />
                    </Input>
                </FormField>
            </VStack>
            <VStack className="md:flex-row z-auto" space="lg">
                <FormField isInsideTab error={formErrors.city?.message} label="City" name="city">
                    <Input isDisabled={isCustomAddress}>
                        <InputField
                            id="city"
                            testID="city"
                            type="text"
                            {...register("city", {
                                required: isCustomAddress ? false : "City is required"
                            })}
                        />
                    </Input>
                </FormField>
                <FormField
                    isInsideTab
                    error={formErrors.region?.message}
                    label="State"
                    name="region"
                >
                    <Input isDisabled={isCustomAddress}>
                        <InputField
                            id="region"
                            testID="region"
                            type="text"
                            {...register("region", {
                                required: isCustomAddress ? false : "State is required"
                            })}
                        />
                    </Input>
                </FormField>
                <FormField
                    isInsideTab
                    error={formErrors.zipcode?.message}
                    label="Zip code"
                    name="zipcode"
                >
                    <Input isDisabled={isCustomAddress}>
                        <InputField
                            id="zipcode"
                            testID="zipcode"
                            type="text"
                            {...register("zipcode", {
                                required: isCustomAddress ? false : "Zipcode is required"
                            })}
                        />
                    </Input>
                </FormField>
                <FormField
                    isInsideTab
                    error={formErrors.country?.message}
                    label="Country"
                    name="country"
                >
                    <Controller
                        control={control}
                        name="country"
                        render={({field: {value}}) => {
                            return (
                                <SelectGeneric
                                    options={optionsCountry}
                                    placeholder="Select Country"
                                    testID="country"
                                    value={value}
                                    onChange={value => setValue("country", value)}
                                />
                            );
                        }}
                        rules={{
                            required: "Country is required"
                        }}
                    />
                </FormField>
            </VStack>

            <FormField
                isInsideTab
                error={formErrors.airbnbAddress?.message}
                label="Address"
                name="airbnbAddress"
                tooltip="This is the way the address will be displayed, such as on the direct booking website and in message rule tags"
            >
                <Controller
                    control={control}
                    name="airbnbAddress"
                    render={({field: {value}}) => {
                        return (
                            <Input
                                isDisabled={!isCustomAddress}
                                leftElement={
                                    <InputSlot>
                                        <Checkbox
                                            isChecked={isCustomAddress}
                                            value="airbnbAddress"
                                            onChange={setIsCustomAddress}
                                        >
                                            <CheckboxIndicator>
                                                <CheckboxIcon />
                                            </CheckboxIndicator>
                                        </Checkbox>
                                    </InputSlot>
                                }
                            >
                                <InputField
                                    className="flex-1"
                                    id="airbnbAddress"
                                    placeholder="Address..."
                                    type="text"
                                    value={value}
                                    onChangeText={value => setValue("airbnbAddress", value)}
                                    {...register("airbnbAddress", {
                                        required: isCustomAddress ? "Address is required" : false
                                    })}
                                />
                            </Input>
                        );
                    }}
                />
            </FormField>

            {debouncedAddress && (
                <Box className="w-full h-64 mt-2 rounded overflow-hidden border border-gray-200">
                    <iframe
                        allowFullScreen
                        height="100%"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(
                            debouncedAddress
                        )}&output=embed`}
                        style={{border: 0}}
                        title="Property location map"
                        width="100%"
                    />
                </Box>
            )}
        </VStack>
    );
};

export default memo(Location);
